/**
 * Capacitor Native Bridge
 * Handles native platform features: location, notifications, deep links
 */
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';

// Check if running in native app
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'

// Force web geolocation for better compatibility with standalone Android build
const useWebGeolocation = true;

/**
 * Geolocation Service
 * Handles foreground and background location
 */
export const LocationService = {
  /**
   * Check and request location permissions
   */
  async checkPermissions() {
    if (!isNative || useWebGeolocation) {
      // Web fallback - use browser geolocation
      return { location: 'granted' };
    }
    
    try {
      const status = await Geolocation.checkPermissions();
      return status;
    } catch (error) {
      console.error('Error checking location permissions:', error);
      return { location: 'denied' };
    }
  },

  /**
   * Request location permissions
   */
  async requestPermissions() {
    if (!isNative || useWebGeolocation) {
      // Web fallback
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve({ location: 'granted' }),
          () => resolve({ location: 'denied' })
        );
      });
    }
    
    try {
      const status = await Geolocation.requestPermissions();
      return status;
    } catch (error) {
      console.error('Error requesting location permissions:', error);
      return { location: 'denied' };
    }
  },

  /**
   * Force the NATIVE OS location runtime permission dialog to appear.
   * Used right after the prominent disclosure so the device permission prompt
   * (e.g. "While using the app" / "Allow all the time") is shown to the user,
   * regardless of the web-geolocation fallback used for actual tracking.
   * Returns true on native (dialog attempted), false on web.
   */
  async requestNativeLocationPermission() {
    if (!isNative) return false;
    try {
      await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] });
    } catch (error) {
      console.error('Error requesting native location permission:', error);
    }
    return true;
  },

  /**
   * Get current position
   */
  async getCurrentPosition(options = {}) {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    };
    
    const opts = { ...defaultOptions, ...options };
    
    if (!isNative || useWebGeolocation) {
      // Web fallback - use navigator.geolocation
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              speed: position.coords.speed,
              heading: position.coords.heading,
            },
            timestamp: position.timestamp,
          }),
          reject,
          opts
        );
      });
    }
    
    return await Geolocation.getCurrentPosition(opts);
  },

  /**
   * Watch position changes (foreground only for now)
   * @param callback Function to call with new positions
   * @returns watchId for clearing later
   */
  watchPosition(callback, options = {}) {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    };
    
    const opts = { ...defaultOptions, ...options };
    
    if (!isNative || useWebGeolocation) {
      // Web fallback - use navigator.geolocation
      return navigator.geolocation.watchPosition(
        (position) => callback({
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            speed: position.coords.speed,
            heading: position.coords.heading,
          },
          timestamp: position.timestamp,
        }),
        (error) => console.error('Location error:', error),
        opts
      );
    }
    
    // Capacitor watch
    return Geolocation.watchPosition(opts, callback);
  },

  /**
   * Clear position watch
   */
  async clearWatch(watchId) {
    if (!isNative) {
      navigator.geolocation.clearWatch(watchId);
      return;
    }
    
    await Geolocation.clearWatch({ id: watchId });
  },

  /**
   * STUB: Start background location tracking
   * Will be implemented in next phase
   */
  async startBackgroundLocation() {
    console.log('[LocationService] Background location stub - not yet implemented');
    // TODO: Implement background geolocation with @capacitor-community/background-geolocation
    return false;
  },

  /**
   * STUB: Stop background location tracking
   */
  async stopBackgroundLocation() {
    console.log('[LocationService] Stop background location stub');
    return true;
  },
};

/**
 * Push Notifications Service
 * Handles notification permissions and registration
 */
export const NotificationService = {
  /**
   * Check notification permissions
   */
  async checkPermissions() {
    if (!isNative) {
      // Web fallback
      if ('Notification' in window) {
        return { receive: Notification.permission };
      }
      return { receive: 'denied' };
    }
    
    try {
      const status = await PushNotifications.checkPermissions();
      return status;
    } catch (error) {
      console.error('Error checking notification permissions:', error);
      return { receive: 'denied' };
    }
  },

  /**
   * Request notification permissions
   */
  async requestPermissions() {
    if (!isNative) {
      // Web fallback
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        return { receive: permission };
      }
      return { receive: 'denied' };
    }
    
    try {
      const status = await PushNotifications.requestPermissions();
      return status;
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
      return { receive: 'denied' };
    }
  },

  /**
   * STUB: Register for push notifications
   * Returns device token for server registration
   */
  async register() {
    if (!isNative) {
      console.log('[NotificationService] Push registration not available on web');
      return null;
    }
    
    console.log('[NotificationService] Push registration stub - not yet implemented');
    // TODO: Implement full push registration
    // await PushNotifications.register();
    return null;
  },

  /**
   * STUB: Add listeners for push notification events
   */
  addListeners(callbacks = {}) {
    if (!isNative) {
      console.log('[NotificationService] Push listeners not available on web');
      return () => {};
    }
    
    console.log('[NotificationService] Push listeners stub - not yet implemented');
    // TODO: Implement push notification listeners
    // - registration
    // - registrationError  
    // - pushNotificationReceived
    // - pushNotificationActionPerformed
    
    return () => {
      // Cleanup function
    };
  },
};

/**
 * Deep Link Service
 * Handles app links and URL schemes
 */
export const DeepLinkService = {
  /**
   * Add listener for app URL opens
   * @param callback Function to call with URL info
   */
  addUrlOpenListener(callback) {
    if (!isNative) {
      // Web doesn't need this - standard routing handles it
      return () => {};
    }
    
    const listener = App.addListener('appUrlOpen', (data) => {
      console.log('[DeepLink] App opened with URL:', data.url);
      callback(data);
    });
    
    return () => listener.remove();
  },

  /**
   * Parse deep link URL and return route info
   */
  parseDeepLink(url) {
    try {
      const parsed = new URL(url);
      const path = parsed.pathname;
      const params = Object.fromEntries(parsed.searchParams);
      
      // Map paths to app routes
      const routes = {
        '/trip': { screen: 'trip', params },
        '/crew': { screen: 'crew', params },
        '/alert': { screen: 'map', params: { ...params, showAlert: true } },
        '/report': { screen: 'reports', params },
        '/friend': { screen: 'crew', params },
      };
      
      for (const [prefix, route] of Object.entries(routes)) {
        if (path.startsWith(prefix)) {
          return route;
        }
      }
      
      // Default to map
      return { screen: 'map', params };
    } catch (error) {
      console.error('[DeepLink] Error parsing URL:', error);
      return { screen: 'map', params: {} };
    }
  },
};

/**
 * App Lifecycle Service
 * Handles app state changes
 */
export const AppLifecycleService = {
  /**
   * Add listener for app state changes
   * @param callback Function called with { isActive: boolean }
   */
  addStateChangeListener(callback) {
    if (!isNative) {
      // Web fallback using visibility API
      const handler = () => {
        callback({ isActive: !document.hidden });
      };
      document.addEventListener('visibilitychange', handler);
      return () => document.removeEventListener('visibilitychange', handler);
    }
    
    const listener = App.addListener('appStateChange', callback);
    return () => listener.remove();
  },

  /**
   * Add listener for back button (Android)
   */
  addBackButtonListener(callback) {
    if (!isNative || platform !== 'android') {
      return () => {};
    }
    
    const listener = App.addListener('backButton', callback);
    return () => listener.remove();
  },
};

/**
 * Battery Service
 * Handles device battery information for Crew Safety tracking
 */
export const BatteryService = {
  /**
   * Get current battery info
   * @returns { batteryLevel: number (0-1), isCharging: boolean }
   */
  async getBatteryInfo() {
    if (!isNative) {
      // Web fallback - Battery Status API (limited support)
      if (navigator.getBattery) {
        try {
          const battery = await navigator.getBattery();
          return {
            batteryLevel: battery.level,
            isCharging: battery.charging
          };
        } catch (error) {
          console.log('[BatteryService] Web Battery API not available');
          return { batteryLevel: null, isCharging: null };
        }
      }
      return { batteryLevel: null, isCharging: null };
    }
    
    try {
      const info = await Device.getBatteryInfo();
      return {
        batteryLevel: info.batteryLevel,
        isCharging: info.isCharging
      };
    } catch (error) {
      console.error('[BatteryService] Error getting battery info:', error);
      return { batteryLevel: null, isCharging: null };
    }
  },

  /**
   * Get device info for debugging
   */
  async getDeviceInfo() {
    if (!isNative) {
      return {
        platform: 'web',
        model: navigator.userAgent
      };
    }
    
    try {
      const info = await Device.getInfo();
      return {
        platform: info.platform,
        model: info.model,
        osVersion: info.osVersion,
        manufacturer: info.manufacturer
      };
    } catch (error) {
      console.error('[BatteryService] Error getting device info:', error);
      return { platform: 'unknown', model: 'unknown' };
    }
  }
};

/**
 * Environment Configuration
 * Handles API URLs and build types
 */
export const EnvConfig = {
  /**
   * Get API base URL based on environment
   */
  getApiBaseUrl() {
    // Check for Capacitor-specific environment
    if (isNative) {
      // Native apps use the production URL
      return process.env.REACT_APP_BACKEND_URL || 'https://api.boat360.app';
    }
    
    // Web uses environment variable
    return process.env.REACT_APP_BACKEND_URL || '';
  },

  /**
   * Get build type
   */
  getBuildType() {
    if (process.env.NODE_ENV === 'production') {
      return 'release';
    }
    return 'debug';
  },

  /**
   * Check if running in development mode
   */
  isDevelopment() {
    return process.env.NODE_ENV === 'development';
  },

  /**
   * Get platform info
   */
  getPlatformInfo() {
    return {
      isNative,
      platform,
      buildType: this.getBuildType(),
      apiBaseUrl: this.getApiBaseUrl(),
    };
  },
};

export default {
  isNative,
  platform,
  LocationService,
  NotificationService,
  DeepLinkService,
  AppLifecycleService,
  BatteryService,
  EnvConfig,
};
